import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  fstJids,
  reasons,
} from '../../../../../data/forms/fst/overtimeDropdownOptions.js';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';

const OvertimeForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    isTech: '',
    penalty: '',
    openCase: '',
    jurisdiction: '',
    caseNumber: '',
    fstNumber: '',
    dateWorked: null,
    overtimeLength: '',
    reason: '',
    comments: '',
    email: user.email,
    emailAddress: user.email,
    userId: user.userId,
    displayName: user.displayName,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleDateWorkedChange = value => {
    setForm({
      ...form,
      dateWorked: value,
    });
  };

  const handleClear = () => {
    setForm({
      ...form,
      isTech: '',
      penalty: '',
      openCase: '',
      jurisdiction: '',
      caseNumber: '',
      fstNumber: '',
      dateWorked: null,
      overtimeLength: '',
      reason: '',
      comments: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_OT_FORM}`,
        data: {
          ...form,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      isTech,
      penalty,
      jurisdiction,
      openCase,
      fstNumber,
      caseNumber,
      dateWorked,
      overtimeLength,
      reason,
    } = form;

    if (
      !isTech ||
      !penalty ||
      !openCase ||
      !jurisdiction ||
      !dateWorked ||
      !overtimeLength ||
      !reason
    ) {
      return false;
    }

    if (openCase === 'Yes' && !caseNumber) {
      return false;
    }

    if (isTech === 'Yes' && !fstNumber) {
      return false;
    }

    return true;
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        display: 'flex',
        justifyItems: 'center',
        width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
      }}
    >
      <Grid size={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <FormControl>
          <FormLabel sx={{ fontFamily: 'BrightstarSans-Bold' }}>
            Are you a Tech?
          </FormLabel>
          <RadioGroup
            name='isTech'
            value={form.isTech}
            onChange={handleChange}
            row
          >
            <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
            <FormControlLabel value='No' control={<Radio />} label='No' />
          </RadioGroup>
        </FormControl>
        <FormControl>
          <FormLabel sx={{ fontFamily: 'BrightstarSans-Bold' }}>
            Case Going to Penalty?
          </FormLabel>
          <RadioGroup
            name='penalty'
            value={form.penalty}
            onChange={handleChange}
            row
          >
            <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
            <FormControlLabel value='No' control={<Radio />} label='No' />
          </RadioGroup>
        </FormControl>
        <FormControl>
          <FormLabel sx={{ fontFamily: 'BrightstarSans-Bold' }}>
            Open Case?
          </FormLabel>
          <RadioGroup
            name='openCase'
            value={form.openCase}
            onChange={handleChange}
            row
          >
            <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
            <FormControlLabel value='No' control={<Radio />} label='No' />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl
          required
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        >
          <InputLabel>
            {translations.jurisdiction || 'Select Form Type'}
          </InputLabel>
          <Select
            value={form.jurisdiction}
            label={translations.jurisdiction || 'Select Form Type'}
            onChange={handleChange}
            name='jurisdiction'
          >
            {fstJids.map(jid => (
              <MenuItem value={jid}>{jid}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='Date Worked*'
            required
            value={form.dateWorked}
            onChange={value => handleDateWorkedChange(value)}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </LocalizationProvider>
        <Tooltip
          title={
            form.isTech !== 'Yes'
              ? "Please select 'Yes' on 'Are you a Tech?' to enable this field"
              : ''
          }
          arrow
        >
          <TextField
            required={form.isTech === 'Yes'}
            disabled={form.isTech !== 'Yes'}
            label='FST Number'
            name='fstNumber'
            fullWidth
            value={form.fstNumber}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
        </Tooltip>
        <Tooltip
          title={
            form.openCase !== 'Yes'
              ? "Please select 'Yes' on Open Case to enable this field"
              : ''
          }
          arrow
        >
          <TextField
            disabled={form.openCase !== 'Yes'}
            required={form.openCase === 'Yes'}
            id='outlined-required'
            label='Case Number'
            name='caseNumber'
            fullWidth
            value={form.caseNumber}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
        </Tooltip>
        <TextField
          label='Associate Name'
          disabled
          value={`${user.firstName} ${user.lastName}`}
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl
          required
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        >
          <InputLabel>{'Reason'}</InputLabel>
          <Select
            value={form.reason}
            label={'Reason'}
            onChange={handleChange}
            name='reason'
          >
            {reasons.map(option => (
              <MenuItem value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label='Overtime Length'
          required
          name='overtimeLength'
          fullWidth
          value={form.overtimeLength}
          onChange={handleChange}
          sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          type='number'
        />
        <TextField
          label='Comments'
          name='comments'
          value={form.comments}
          onChange={handleChange}
          multiline
          rows={4.1}
          sx={{
            backgroundColor: '#ffffff',
            marginBottom: '15px',
            width: '100%',
          }}
        />
      </Grid>
      <Grid
        size={12}
        sx={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant='contained'
          sx={{
            backgroundColor: 'var(--aldebaran-yellow)',
            color: '#000000',
            fontFamily: 'BrightstarSans-Regular',
            width: '10%',
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
        <Tooltip
          title={
            !isFormValid()
              ? 'Please fill in all required fields to enable the Submit button'
              : ''
          }
          arrow
        >
          <Box>
            <Button
              disabled={!isFormValid() || isSubmitting}
              variant='contained'
              sx={{
                backgroundColor: 'var(--vega-blue)',
                fontFamily: 'BrightstarSans-Regular',
                width: '100%',
              }}
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              {'Submit'}
            </Button>
          </Box>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default OvertimeForm;
